// ============================================================================
// ADMIN BOOKING EMAILS — system admins edit the account-manager email
// templates (per email type, with optional per-session-type versions), the
// reminder/survey timing, and preview or test-send against a real booking.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mail, Loader, AlertCircle, Save, Send, RotateCcw, Copy, Eye, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  BookingEmailType,
  BookingEmailSettings,
  TemplatesResponse,
  SampleBooking,
  EMAIL_TYPES,
  EMAIL_TYPE_LABELS,
  EMAIL_TYPE_HINTS,
  DEFAULT_TEMPLATE_KEY,
  getEmailTemplates,
  saveEmailTemplate,
  resetEmailTemplate,
  getEmailSettings,
  saveEmailSettings,
  listSampleBookings,
  previewEmail,
  sendTestEmail
} from '../services/booking-emails';

const inputCls = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';
const labelCls = 'block text-sm font-semibold text-navy mb-2';
const sectionLabel = 'text-[11px] font-bold text-primary tracking-widest uppercase';

type CellState = 'default' | 'custom-on' | 'custom-off' | 'default-on' | 'default-off';

function SettingsCard() {
  const [settings, setSettings] = useState<BookingEmailSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getEmailSettings().then(setSettings).catch(() => toast.error('Failed to load settings')); }, []);

  async function save() {
    if (!settings) return;
    setSaving(true);
    try {
      setSettings(await saveEmailSettings(settings));
      toast.success('Settings saved');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  if (!settings) return null;
  const num = (key: keyof BookingEmailSettings) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setSettings({ ...settings, [key]: Number(e.target.value) });
  const hours = Array.from({ length: 24 }, (_, h) => h);

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <div className="p-5 border-b border-[#e2e8f0] flex items-center justify-between">
        <div>
          <p className={sectionLabel}>Timing and survey</p>
          <p className="text-xs text-slate-400 mt-1">
            Times are Europe/Dublin. Working days skip weekends but not bank holidays.
            {settings.updatedByName && <> Last changed by {settings.updatedByName}.</>}
          </p>
        </div>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 rounded-xl h-10 px-5 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm disabled:opacity-60">
          {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />} Save
        </button>
      </div>
      <div className="p-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className={labelCls}>Reminder: working days before</label>
          <input type="number" min={1} max={20} value={settings.reminderWorkingDaysBefore} onChange={num('reminderWorkingDaysBefore')} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Reminder send time</label>
          <select value={settings.reminderSendHour} onChange={num('reminderSendHour')} className={`${inputCls} bg-white`}>
            {hours.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Survey send time</label>
          <select value={settings.surveySendHour} onChange={num('surveySendHour')} className={`${inputCls} bg-white`}>
            {hours.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Survey link</label>
          <input type="url" value={settings.surveyUrl} placeholder="https://… (empty = no survey emails)"
            onChange={e => setSettings({ ...settings, surveyUrl: e.target.value })} className={inputCls} />
        </div>
      </div>
    </div>
  );
}

export default function AdminBookingEmails() {
  const [data, setData] = useState<TemplatesResponse | null>(null);
  const [samples, setSamples] = useState<SampleBooking[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [emailType, setEmailType] = useState<BookingEmailType>('confirmation');
  const [eventTypeId, setEventTypeId] = useState<string>(DEFAULT_TEMPLATE_KEY);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  const [sampleId, setSampleId] = useState('');
  const [preview, setPreview] = useState<{ subject: string; html: string; unknownPlaceholders: string[] } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [testing, setTesting] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    try {
      const [templates, sampleBookings] = await Promise.all([getEmailTemplates(), listSampleBookings()]);
      setData(templates);
      setSamples(sampleBookings);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load booking email templates');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const find = useCallback((type: BookingEmailType, evt: string) =>
    data?.templates.find(t => t.emailType === type && t.eventTypeId === evt), [data]);

  const saved = find(emailType, eventTypeId);
  const savedDefault = find(emailType, DEFAULT_TEMPLATE_KEY);
  const isDefault = eventTypeId === DEFAULT_TEMPLATE_KEY;
  const usesDefault = !isDefault && !saved;
  const baseline = saved || (isDefault ? { ...data?.defaults[emailType], enabled: false } : undefined);

  // Load the selected template into the editor
  useEffect(() => {
    if (!data) return;
    const source = saved || (isDefault ? { ...data.defaults[emailType], enabled: false } : null);
    setSubject(source?.subject || '');
    setBody(source?.bodyMarkdown || '');
    setEnabled(!!source?.enabled);
    setPreview(null);
  }, [data, emailType, eventTypeId, saved, isDefault]);

  // Prefer a sample booking of the selected session type
  useEffect(() => {
    if (!samples.length) return;
    const match = !isDefault && samples.find(s => s.eventTypeId === eventTypeId);
    setSampleId(prev => (match ? match.bookingId : prev || samples[0].bookingId));
  }, [samples, eventTypeId, isDefault]);

  const dirty = !!baseline && (subject !== baseline.subject || body !== baseline.bodyMarkdown || enabled !== !!baseline.enabled);

  function cellState(type: BookingEmailType, evt: string): CellState {
    const own = find(type, evt);
    if (evt === DEFAULT_TEMPLATE_KEY) return own?.enabled ? 'default-on' : 'default-off';
    if (own) return own.enabled ? 'custom-on' : 'custom-off';
    return 'default';
  }

  function customise() {
    const source = savedDefault || { ...data!.defaults[emailType], enabled: false };
    setSubject(source.subject);
    setBody(source.bodyMarkdown);
    setEnabled(!!source.enabled);
    setData(d => d && {
      ...d,
      templates: [...d.templates, {
        emailType, eventTypeId, subject: source.subject, bodyMarkdown: source.bodyMarkdown,
        enabled: !!source.enabled, updatedByName: 'unsaved', updatedAt: ''
      }]
    });
  }

  async function save() {
    setSaving(true);
    try {
      const template = await saveEmailTemplate(emailType, eventTypeId, { subject, bodyMarkdown: body, enabled });
      setData(d => d && {
        ...d,
        templates: [...d.templates.filter(t => !(t.emailType === emailType && t.eventTypeId === eventTypeId)), template]
      });
      toast.success('Template saved');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  }

  async function revert() {
    const message = isDefault
      ? 'Reset this template to the built-in draft? It will be switched off.'
      : 'Remove the custom version? This session type will use the default template again.';
    if (!window.confirm(message)) return;
    try {
      if (saved?.updatedAt) await resetEmailTemplate(emailType, eventTypeId);
      setData(d => d && { ...d, templates: d.templates.filter(t => !(t.emailType === emailType && t.eventTypeId === eventTypeId)) });
      toast.success(isDefault ? 'Reset to the built-in draft' : 'Now using the default template');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to reset template');
    }
  }

  async function runPreview() {
    if (!sampleId) return toast.error('Choose a booking to preview against');
    setPreviewing(true);
    try {
      setPreview(await previewEmail({ emailType, subject, bodyMarkdown: body, bookingId: sampleId }));
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to render preview');
    } finally {
      setPreviewing(false);
    }
  }

  async function runTest() {
    if (!sampleId) return toast.error('Choose a booking to test with');
    setTesting(true);
    try {
      const { sentTo } = await sendTestEmail({ emailType, subject, bodyMarkdown: body, bookingId: sampleId });
      toast.success(`Test email sent to ${sentTo}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  }

  function insertPlaceholder(key: string) {
    const token = `{{${key}}}`;
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  }

  const sessionTypes = useMemo(() => [
    { eventTypeId: DEFAULT_TEMPLATE_KEY, name: 'Default (all session types)', isActive: true },
    ...(data?.eventTypes || []).sort((a, b) => a.name.localeCompare(b.name))
  ], [data]);

  const CELL_STYLES: Record<CellState, { label: string; cls: string }> = {
    'default': { label: 'Default', cls: 'bg-slate-50 text-slate-400 border-slate-200' },
    'default-on': { label: 'On', cls: 'bg-green-50 text-green-700 border-green-200' },
    'default-off': { label: 'Off', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
    'custom-on': { label: 'Custom', cls: 'bg-primary/10 text-primary border-primary/30' },
    'custom-off': { label: 'Custom · off', cls: 'bg-amber-50 text-amber-700 border-amber-200' }
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Mail size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Booking emails</h1>
              <p className="text-slate-500 text-sm font-medium">
                Emails to the account team lead, sent from the co-creation mailbox for bookings that opt in
              </p>
            </div>
          </div>
        </header>

        {error && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <SettingsCard />

        {!data ? (
          <div className="flex justify-center py-20 text-slate-400"><Loader className="w-6 h-6 animate-spin" /></div>
        ) : (
          <>
            {/* Which template applies where */}
            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              <div className="p-5 border-b border-[#e2e8f0]">
                <p className={sectionLabel}>Templates by session type</p>
                <p className="text-xs text-slate-400 mt-1">
                  A session type uses the default template unless it has its own version. A custom version that is off sends nothing for that session type. Click a cell to edit.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Session type</th>
                      {EMAIL_TYPES.map(t => (
                        <th key={t} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{EMAIL_TYPE_LABELS[t]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e2e8f0]">
                    {sessionTypes.map(st => (
                      <tr key={st.eventTypeId}>
                        <td className={`px-4 py-2 ${st.eventTypeId === DEFAULT_TEMPLATE_KEY ? 'font-semibold text-navy' : 'text-slate-600'}`}>
                          {st.name}{!st.isActive && <span className="text-xs text-slate-400"> (inactive)</span>}
                        </td>
                        {EMAIL_TYPES.map(t => {
                          const state = cellState(t, st.eventTypeId);
                          const selected = t === emailType && st.eventTypeId === eventTypeId;
                          return (
                            <td key={t} className="px-4 py-2">
                              <button onClick={() => { setEmailType(t); setEventTypeId(st.eventTypeId); }}
                                className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${CELL_STYLES[state].cls} ${selected ? 'ring-2 ring-navy ring-offset-1' : ''}`}>
                                {CELL_STYLES[state].label}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Editor */}
            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              <div className="flex items-center gap-1 px-4 pt-3 border-b border-[#e2e8f0]">
                {EMAIL_TYPES.map(t => (
                  <button key={t} onClick={() => setEmailType(t)}
                    className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                      emailType === t ? 'border-primary text-navy' : 'border-transparent text-slate-500 hover:text-navy'
                    }`}>
                    {EMAIL_TYPE_LABELS[t]}
                  </button>
                ))}
              </div>

              <div className="p-5 border-b border-[#e2e8f0] flex flex-wrap items-center gap-3">
                <select value={eventTypeId} onChange={e => setEventTypeId(e.target.value)} className={`${inputCls} bg-white max-w-sm`}>
                  {sessionTypes.map(st => <option key={st.eventTypeId} value={st.eventTypeId}>{st.name}</option>)}
                </select>
                <p className="text-xs text-slate-500">{EMAIL_TYPE_HINTS[emailType]}</p>
              </div>

              {usesDefault ? (
                <div className="p-10 text-center">
                  <p className="text-sm text-slate-600">This session type uses the default {EMAIL_TYPE_LABELS[emailType].toLowerCase()} template
                    {savedDefault?.enabled ? '' : ', which is currently off'}.</p>
                  <button onClick={customise}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl h-10 px-5 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm">
                    <Copy size={16} /> Customise for this session type
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px]">
                  <div className="p-5 flex flex-col gap-4 xl:border-r border-[#e2e8f0]">
                    <div className="flex items-center justify-between gap-3">
                      <label className="flex items-center gap-2 text-sm font-semibold text-navy cursor-pointer">
                        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="w-4 h-4 accent-primary rounded" />
                        Send this email
                      </label>
                      <span className="text-xs text-slate-400">
                        {saved?.updatedAt ? `Last edited by ${saved.updatedByName}, ${new Date(saved.updatedAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}` : 'Not saved yet (built-in draft)'}
                        {dirty && <span className="text-amber-600 font-semibold"> · unsaved changes</span>}
                      </span>
                    </div>
                    <div>
                      <label className={labelCls}>Subject</label>
                      <input value={subject} onChange={e => setSubject(e.target.value)} maxLength={200} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Body (markdown)</label>
                      <textarea ref={bodyRef} value={body} onChange={e => setBody(e.target.value)} rows={22} maxLength={20000}
                        className={`${inputCls} font-mono text-xs leading-relaxed resize-y`} />
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <button onClick={save} disabled={saving || !subject.trim() || !body.trim()}
                        className="flex items-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm disabled:opacity-60">
                        {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />} Save template
                      </button>
                      <button onClick={revert}
                        className="flex items-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm">
                        <RotateCcw size={16} /> {isDefault ? 'Reset to built-in draft' : 'Revert to default'}
                      </button>
                    </div>
                  </div>

                  <div className="p-5 flex flex-col gap-5 bg-slate-50/50">
                    <div>
                      <p className={`${sectionLabel} mb-2`}>Preview with a booking</p>
                      <select value={sampleId} onChange={e => setSampleId(e.target.value)} className={`${inputCls} bg-white`}>
                        {samples.map(s => (
                          <option key={s.bookingId} value={s.bookingId}>{s.startDate} · {s.customer} · {s.eventTypeName}</option>
                        ))}
                      </select>
                      <div className="flex gap-2 mt-2">
                        <button onClick={runPreview} disabled={previewing}
                          className="flex-1 flex items-center justify-center gap-2 rounded-xl h-9 border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50 disabled:opacity-60">
                          {previewing ? <Loader size={14} className="animate-spin" /> : <Eye size={14} />} Preview
                        </button>
                        <button onClick={runTest} disabled={testing}
                          className="flex-1 flex items-center justify-center gap-2 rounded-xl h-9 border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50 disabled:opacity-60">
                          {testing ? <Loader size={14} className="animate-spin" /> : <Send size={14} />} Send test to me
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1">Uses the unsaved text above. The test goes only to you.</p>
                    </div>

                    <div>
                      <p className={`${sectionLabel} mb-2`}>Placeholders</p>
                      <div className="flex flex-col gap-1 max-h-72 overflow-y-auto">
                        {data.placeholders.map(p => (
                          <button key={p.key} onClick={() => insertPlaceholder(p.key)} title="Insert at cursor"
                            className="text-left px-2 py-1 rounded-lg hover:bg-white group">
                            <code className="text-xs text-primary">{`{{${p.key}}}`}</code>
                            <span className="block text-[11px] text-slate-500">{p.description}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {preview && !usesDefault && (
                <div className="border-t border-[#e2e8f0] p-5 flex flex-col gap-3">
                  <p className={sectionLabel}>Preview</p>
                  {preview.unknownPlaceholders.length > 0 && (
                    <p className="text-xs text-amber-700 flex items-center gap-1">
                      <AlertCircle size={12} /> Unknown placeholders (left blank): {preview.unknownPlaceholders.map(k => `{{${k}}}`).join(', ')}
                    </p>
                  )}
                  <p className="text-sm text-navy"><span className="text-slate-400">Subject:</span> {preview.subject}</p>
                  <iframe title="Email preview" sandbox="" srcDoc={preview.html}
                    className="w-full h-[520px] rounded-xl border border-[#e2e8f0] bg-white" />
                  <p className="text-[11px] text-slate-400 flex items-center gap-1"><Check size={11} /> Rendered by the server exactly as it would be sent.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
